import { apiFetch } from "../api.js";

/**
 * Estate api-module — Phase 9 (2026-05-08).
 *
 * Mirror types of `shared/src/schemas/estate.ts`. Web does NOT import
 * from @hearth/shared (5.1 lock + 8.2 server-only-subpath refinement);
 * we keep a parallel browser-safe definition here.
 */

export type EstateDocumentType =
  | "will"
  | "revocable_trust"
  | "healthcare_proxy"
  | "hipaa_release"
  | "financial_poa"
  | "guardianship_designation"
  | "advance_directive";

export type EstateDocumentStatus =
  | "needed"
  | "drafted"
  | "executed"
  | "reviewed";

export type EstateKeyContactRole =
  | "attorney"
  | "accountant"
  | "doctor"
  | "funeral_home"
  | "executor"
  | "guardian"
  | "other";

export type BurialPreference = "burial" | "cremation" | "donation";

export type EstateItemType =
  | "document"
  | "letter_of_intent"
  | "burial_wishes"
  | "digital_asset"
  | "key_contact";

export interface EstateDocumentItem {
  item_type: "document";
  household_id: string;
  item_id: string;
  document_type: EstateDocumentType;
  status: EstateDocumentStatus;
  display_name: string;
  notes?: string;
  s3_object_key?: string;
  s3_uploaded_at?: string;
  original_filename?: string;
  content_type?: string;
  byte_size?: number;
  drafted_at?: string;
  executed_at?: string;
  last_reviewed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface EstateLetterOfIntentItem {
  item_type: "letter_of_intent";
  household_id: string;
  item_id: string;
  body_present?: boolean;
  body_byte_size?: number;
  created_at: string;
  updated_at: string;
}

export interface EstateBurialWishesItem {
  item_type: "burial_wishes";
  household_id: string;
  item_id: string;
  burial_preference: BurialPreference;
  funeral_home?: string;
  plot_location?: string;
  religious_considerations?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface EstateDigitalAssetItem {
  item_type: "digital_asset";
  household_id: string;
  item_id: string;
  display_name: string;
  linked_account_id?: string;
  recovery_instructions: string;
  created_at: string;
  updated_at: string;
}

export interface EstateKeyContactItem {
  item_type: "key_contact";
  household_id: string;
  item_id: string;
  contact_role: EstateKeyContactRole;
  name: string;
  phone?: string;
  email?: string;
  organization?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export type EstateItem =
  | EstateDocumentItem
  | EstateLetterOfIntentItem
  | EstateBurialWishesItem
  | EstateDigitalAssetItem
  | EstateKeyContactItem;

export interface ListEstateItemsResponse {
  items: EstateItem[];
  count: number;
}

export interface BeneficiaryAuditFlaggedAccount {
  account_id: string;
  account_name: string;
  account_type: string;
  flags: ("missing" | "stale" | "estate_listed")[];
}

export interface BeneficiaryAudit {
  household_id: string;
  audit_id: string;
  timestamp: string;
  audit_date: string;
  total_accounts_examined: number;
  clean_count: number;
  flagged_count: number;
  flagged_accounts: BeneficiaryAuditFlaggedAccount[];
  summary: string;
  triggered_by: "cron" | "manual";
}

export interface ListAuditsResponse {
  audits: BeneficiaryAudit[];
  count: number;
}

export interface PresignedUploadResponse {
  upload_url: string;
  expires_in_seconds: number;
  s3_object_key: string;
  required_headers: Record<string, string>;
}

export interface PresignedDownloadResponse {
  download_url: string;
  expires_in_seconds: number;
}

export interface LetterOfIntentResponse {
  body: string | null;
  item_id: string | null;
  updated_at: string | null;
  byte_size?: number;
}

// === API surface ==========================================================

export function listEstateItems(): Promise<ListEstateItemsResponse> {
  return apiFetch<ListEstateItemsResponse>("/v1/estate");
}

export interface CreateDocumentInput {
  item_type: "document";
  document_type: EstateDocumentType;
  status: EstateDocumentStatus;
  display_name: string;
  notes?: string;
}

export interface CreateBurialWishesInput {
  item_type: "burial_wishes";
  burial_preference: BurialPreference;
  funeral_home?: string | undefined;
  plot_location?: string | undefined;
  religious_considerations?: string | undefined;
  notes?: string | undefined;
}

export interface CreateDigitalAssetInput {
  item_type: "digital_asset";
  display_name: string;
  linked_account_id?: string | undefined;
  recovery_instructions: string;
}

export interface CreateKeyContactInput {
  item_type: "key_contact";
  contact_role: EstateKeyContactRole;
  name: string;
  phone?: string | undefined;
  email?: string | undefined;
  organization?: string | undefined;
  notes?: string | undefined;
}

export type CreateEstateItemInput =
  | CreateDocumentInput
  | CreateBurialWishesInput
  | CreateDigitalAssetInput
  | CreateKeyContactInput;

export function createEstateItem(
  input: CreateEstateItemInput,
): Promise<EstateItem> {
  return apiFetch<EstateItem>("/v1/estate", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateEstateItem(
  itemId: string,
  patch: Record<string, unknown>,
): Promise<EstateItem> {
  return apiFetch<EstateItem>(`/v1/estate/${encodeURIComponent(itemId)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deleteEstateItem(itemId: string): Promise<void> {
  return apiFetch<void>(`/v1/estate/${encodeURIComponent(itemId)}`, {
    method: "DELETE",
  });
}

export function getEstateUploadUrl(
  itemId: string,
  payload: { filename: string; content_type: string; byte_size?: number },
): Promise<PresignedUploadResponse> {
  return apiFetch<PresignedUploadResponse>(
    `/v1/estate/${encodeURIComponent(itemId)}/upload-url`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function getEstateDownloadUrl(
  itemId: string,
): Promise<PresignedDownloadResponse> {
  return apiFetch<PresignedDownloadResponse>(
    `/v1/estate/${encodeURIComponent(itemId)}/download-url`,
  );
}

export function getLetterOfIntent(): Promise<LetterOfIntentResponse> {
  return apiFetch<LetterOfIntentResponse>("/v1/estate/letter-of-intent");
}

export function saveLetterOfIntent(
  body: string,
): Promise<{ item_id: string; updated_at: string; byte_size: number }> {
  return apiFetch<{ item_id: string; updated_at: string; byte_size: number }>(
    "/v1/estate/letter-of-intent",
    { method: "POST", body: JSON.stringify({ body }) },
  );
}

export function listBeneficiaryAudits(): Promise<ListAuditsResponse> {
  return apiFetch<ListAuditsResponse>("/v1/estate/audits");
}

export function compileEmergencyDoc(): Promise<{
  status: string;
  message: string;
}> {
  return apiFetch<{ status: string; message: string }>(
    "/v1/estate/emergency-doc/compile",
    { method: "POST", body: JSON.stringify({}) },
  );
}
